import { Map } from 'immutable'
import * as React from 'react'
import {
  Action,
  any,
  async,
  AsyncState,
  IOWidget,
  loadedAsyncState,
  loadingAsyncState,
  mk_widget,
  Pair,
  stateful
} from 'widgets-for-react'
import { getClassName, link, linkWrapper } from '../../../../cms/blocks/utils.gen'
import { TemplateState } from '../../../../cms/page/types.gen'
import { CustomBlock } from '../../../../wordpress/types_and_parser/custom_blocks.gen'
import { Block } from '../../../../wordpress/types_and_parser/standard_blocks.gen'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { DataLayer } from '../../../utils/datalayer'
import { CustomBlockData } from '../types.mod'
import { getCases, WordPress } from '../../../../wordpress/wordpress_api'
import { ImageContainer } from '../../../../cms/blocks/widgets/ImageContainer.gen'
import { ImageSize } from '../../../../cms/blocks/types.gen'
import Slider from 'react-slick'

const SliderSettings = {
  dots: true,
  arrows: true,
  infinite: true,
  speed: 2000,
  slidesToShow: 1,
  slidesToScroll: 1,
  fade: false
}

export type BSLCaseSliderBlockProps<extra_images> = {
  categories: number[]
  size?: ImageSize<extra_images>
  get_default_data_src: () => ImageSize<extra_images>
  get_default_data_srcset: () => Array<Pair<ImageSize<extra_images>, string>>
}

export type BSLCaseSliderBlockState<extra_images> = {
  cases: AsyncState<WordPress.CaseResponse[]>
  images: Map<number, AsyncState<Map<ImageSize<extra_images>, string>>>
}

export type BSLCaseSliderBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  content_classname: string
  list_classname: string
  item_classname: string
  item_inner_classname: string
  item_image_classname: string
  item_figure_classname: string
  item_placeholder_classname: string
  item_content_classname: string
  item_header_classname: string
  item_label_classname: string
  item_title_classname: string
  item_intro_classname: string
  item_footer_classname: string
}>

function loadImage<extra_images>(image: { id: number; sizes: { [P in string]: string } }) {
  let map = Map<ImageSize<extra_images>, string>()
  Object.entries(image.sizes).forEach(([n, s]) => {
    map = map.set(n as ImageSize<extra_images>, s)
  })
  return map
}

function mapImages<extra_images>(cases: WordPress.CaseResponse[]) {
  return cases.reduce((acc, i) => {
    if (i.acf.kind_media == 'image') {
      return acc.set(i.acf.image.id, loadedAsyncState(loadImage(i.acf.image)))
    }
    return acc
  }, Map<number, AsyncState<Map<ImageSize<extra_images>, string>>>())
}

export function BSLCaseSliderBlock<extra_images>(
  props: BSLCaseSliderBlockProps<extra_images>,
  options?: { style?: BSLCaseSliderBlockStyle; key?: string }
): IOWidget<
  TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>,
  Action<TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>>
> {
  return (s: TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>) =>
    stateful<BSLCaseSliderBlockState<extra_images>>()(s0 =>
      any<Action<BSLCaseSliderBlockState<extra_images>>>()([
        async<WordPress.CaseResponse[]>()(s0.cases).map(a => s1 => {
          const s2 = a(s1.cases)
          return {
            ...s1,
            cases: s2,
            images: s2.kind == 'loaded' ? mapImages(s2.value) : s1.images
          }
        }),
        mk_widget({
          run: setState => {
            if (s0.cases.kind == 'loaded' && s0.cases.value.length <= 0) {
              return <></>
            }
            return (
              <section
                className={getClassName(options, 'block_classname', 'block')}
                key={options && options.key ? options.key : ''}
              >
                <div className={getClassName(options, 'inner_classname', '')}>
                  <div className={getClassName(options, 'content_classname', '')} id="js-case-content">
                    <Slider className={getClassName(options, 'list_classname', '')} {...SliderSettings}>
                      {s0.cases.kind === 'loaded' ? (
                        s0.cases.value.map((item, key, array) => (
                          <div className={getClassName(options, 'item_classname', '')}>
                            <div className={getClassName(options, 'item_inner_classname', '')}>
                              <div className={getClassName(options, 'item_content_classname', '')}>
                                <header className={getClassName(options, 'item_header_classname', '')}>
                                  <span className={getClassName(options, 'item_label_classname', '')}>{item.type}</span>
                                  <h2 className={getClassName(options, 'item_title_classname', '')}>
                                    {item.title.rendered}
                                  </h2>
                                  {item.acf.hero_intro && (
                                    <div
                                      className={getClassName(options, 'item_intro_classname', '')}
                                      dangerouslySetInnerHTML={{ __html: item.acf.hero_intro }}
                                    />
                                  )}
                                </header>
                                <footer className={getClassName(options, 'item_footer_classname', '')}>
                                  {link(
                                    linkWrapper({ title: item.title.rendered, url: item.link }),
                                    { classname: 'case__link button--secondary' },
                                    'Lees meer'
                                  )}
                                </footer>
                              </div>
                              {item.acf && item.acf.image && item.acf.image.id && s0.images.has(item.acf.image.id) && 
                                <figure className={getClassName(options, 'item_figure_classname', ``)}>
                                  {ImageContainer(
                                    s0.images.get(item.acf.image.id) as AsyncState<
                                      Map<ImageSize<extra_images>, string>
                                    >,
                                    props.size,
                                    getClassName(options, 'item_image_classname', 'w-100'),
                                    item.acf.image_alt,
                                    props.get_default_data_src,
                                    props.get_default_data_srcset
                                  ).run(_ => {})}
                                </figure>
                              }
                            </div>
                          </div>
                        ))
                      ) : (
                        <div>
                          <div className="svg-loader svg-loader--nobg">
                            <svg viewBox="0 0 40 40" className="svg-loader__svg">
                              <path
                                fill="#ccc"
                                className="svg-loader__background"
                                d="M20.201,5.169c-8.254,0-14.946,6.692-14.946,14.946c0,8.255,6.692,14.946,14.946,14.946
                                      s14.946-6.691,14.946-14.946C35.146,11.861,28.455,5.169,20.201,5.169z M20.201,31.749c-6.425,0-11.634-5.208-11.634-11.634
                                      c0-6.425,5.209-11.634,11.634-11.634c6.425,0,11.633,5.209,11.633,11.634C31.834,26.541,26.626,31.749,20.201,31.749z"
                              />
                              <path
                                fill="#000"
                                className="svg-loader__foreground"
                                d="M26.013,10.047l1.654-2.866c-2.198-1.272-4.743-2.012-7.466-2.012h0v3.312h0 C22.32,8.481,24.301,9.057,26.013,10.047z"
                              >
                                <animateTransform
                                  attributeType="xml"
                                  attributeName="transform"
                                  type="rotate"
                                  from="0 20 20"
                                  to="360 20 20"
                                  dur="0.9s"
                                  repeatCount="indefinite"
                                />
                              </path>
                            </svg>
                          </div>
                        </div>
                      )}
                    </Slider>
                  </div>
                </div>
              </section>
            )
          }
        })
      ]).map(a => a(s0))
    )({
      cases: loadingAsyncState(() => getCases(props.categories)),
      images: Map()
    }).map(a => s => s)
}
